import { createPortal } from "preact/compat";
import type { ComponentChildren } from "preact";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "preact/hooks";
import {
  Check,
  Clock3,
  History,
  LoaderCircle,
  Minus,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import type { MapComparison, MapHistoryEntry } from "../shared/history";
import { api, errorText } from "./api";
import { Loading, Notice } from "./ui";
import "./map-review-dialogs.css";

interface ReviewDialogProps {
  title: string;
  description: string;
  busy?: boolean;
  returnFocusTo?: HTMLElement | null;
  onClose: () => void;
  children: ComponentChildren;
  footer: ComponentChildren;
}

export function ReviewDialog({
  title,
  description,
  busy = false,
  returnFocusTo,
  onClose,
  children,
  footer,
}: ReviewDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const returnFocusRef = useRef(returnFocusTo);
  returnFocusRef.current = returnFocusTo;
  const closing = useRef(false);
  const requestClose = () => {
    if (busy || closing.current) return;
    closing.current = true;
    closeRef.current();
  };

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    // Start at the title so screen-reader users hear the context before actions.
    dialog.querySelector<HTMLElement>("h2")?.focus({ preventScroll: true });
    return () => {
      closing.current = true;
      dialog.close();
      document.body.style.overflow = overflow;
      queueMicrotask(() => {
        const target = returnFocusRef.current?.isConnected
          ? returnFocusRef.current
          : opener instanceof HTMLElement && opener.isConnected
            ? opener
            : null;
        target?.focus({ preventScroll: true });
      });
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className="em-map-review-dialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-busy={busy || undefined}
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onClose={requestClose}
      onKeyDown={(event) => {
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === "s"
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <header className="em-map-review-header">
        <div>
          <h2 id={titleId} tabIndex={-1}>
            {title}
          </h2>
          <p id={descriptionId}>{description}</p>
        </div>
        <button
          type="button"
          className="em-icon-button"
          aria-label="Close review"
          onClick={requestClose}
          disabled={busy}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </header>
      <div className="em-map-review-body">{children}</div>
      <footer className="em-map-review-footer">{footer}</footer>
    </dialog>,
    document.body,
  );
}

const fieldNames: Record<string, string> = {
  id: "Identifier",
  label: "Label",
  name: "Name",
  points: "Shape",
  polygon: "Shape",
  height: "Height",
  x: "Horizontal position",
  y: "Vertical position",
  hidden: "Visibility",
  logo: "Logo",
  labelMode: "Label layout",
  labelRotation: "Label rotation",
  fill: "Colour",
  color: "Colour",
  route: "Navigation",
  navigation: "Navigation",
  title: "Map title",
  subtitle: "Description",
  geometry: "Floor plan",
  locations: "Locations",
  camera: "Starting view",
  presentation: "Presentation",
  routing: "Wayfinding",
  entrances: "Entrances",
  architecture: "Walls and doors",
  floor: "Floor",
  settings: "Settings",
  theme: "Appearance",
};
function fieldLabel(field: string) {
  return (
    fieldNames[field] ||
    field
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_.-]/g, " ")
      .replace(/^./, (character) => character.toUpperCase())
  );
}

function ComparisonGroup({
  title,
  group,
}: {
  title: string;
  group: MapComparison["booths"];
}) {
  const total = group.added + group.removed + group.changed;
  return (
    <section className="em-map-review-group" aria-label={`${title} changes`}>
      <div className="em-map-review-group-heading">
        <h3>{title}</h3>
        {!total && (
          <span className="em-map-review-unchanged">
            <Check size={13} aria-hidden="true" />
            Unchanged
          </span>
        )}
      </div>
      {total > 0 && (
        <>
          <div className="em-map-review-counts">
            {group.added > 0 && (
              <span data-change="added">
                <Plus size={13} aria-hidden="true" />
                {group.added} added
              </span>
            )}
            {group.changed > 0 && (
              <span data-change="changed">
                <RefreshCw size={12} aria-hidden="true" />
                {group.changed} changed
              </span>
            )}
            {group.removed > 0 && (
              <span data-change="removed">
                <Minus size={13} aria-hidden="true" />
                {group.removed} removed
              </span>
            )}
          </div>
          <details className="em-map-review-details">
            <summary>
              Show {title.toLowerCase().replace(/s$/, "")} changes
            </summary>
            <ul>
              {group.items.map((item) => (
                <li key={`${item.change}:${item.id}`}>
                  <div>
                    <strong>{item.label || item.id}</strong>
                    <span
                      className="em-map-review-change"
                      data-change={item.change}
                    >
                      {item.change}
                    </span>
                  </div>
                  <p>
                    {[
                      ...(item.label && item.label !== item.id
                        ? [item.id]
                        : []),
                      ...item.fields.map(fieldLabel),
                    ].join(" · ") ||
                      (item.change === "removed"
                        ? "Removed from map"
                        : "New entry")}
                  </p>
                </li>
              ))}
            </ul>
            {group.truncated && (
              <p className="em-map-review-hint">
                Showing the first {group.items.length} changes. The totals above
                include every change.
              </p>
            )}
          </details>
        </>
      )}
    </section>
  );
}

const reviewCopy = {
  source: {
    title: "Review source changes",
    description:
      "Review the differences before applying the source update to your saved draft. Your published map stays unchanged until you publish.",
    action: "Apply to draft",
    pending: "Applying…",
  },
  publish: {
    title: "Review publication",
    description:
      "These are the differences between the saved draft and the published map. Publishing makes this version available to embedded maps.",
    action: "Publish map",
    pending: "Publishing…",
  },
  recovery: {
    title: "Review recovered draft",
    description:
      "A newer saved draft exists. Review these differences before bringing back your recovered edits. This updates the editor only; save when you are ready.",
    action: "Use recovered draft",
    pending: "Recovering…",
  },
};

export interface MapChangeReviewProps {
  kind: keyof typeof reviewCopy;
  comparison: MapComparison;
  busy?: boolean;
  error?: string;
  returnFocusTo?: HTMLElement | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function MapChangeReview({
  kind,
  comparison,
  busy = false,
  error,
  returnFocusTo,
  onConfirm,
  onClose,
}: MapChangeReviewProps) {
  const copy = reviewCopy[kind];
  return (
    <ReviewDialog
      title={copy.title}
      description={copy.description}
      busy={busy}
      returnFocusTo={returnFocusTo}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="em-button em-button-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="em-button"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy && (
              <LoaderCircle size={15} className="em-spin" aria-hidden="true" />
            )}
            {busy ? copy.pending : copy.action}
          </button>
        </>
      }
    >
      {error && <Notice>{error}</Notice>}
      {!comparison.hasChanges ? (
        <p className="em-map-review-empty">
          <Check size={19} aria-hidden="true" />
          No content differences.
        </p>
      ) : (
        <>
          <ComparisonGroup title="Booths" group={comparison.booths} />
          <ComparisonGroup title="Places" group={comparison.locations} />
          {comparison.settings.length > 0 && (
            <section className="em-map-review-group">
              <h3>Map settings</h3>
              <ul className="em-map-review-settings">
                {comparison.settings.map((setting) => (
                  <li key={setting}>{fieldLabel(setting)}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </ReviewDialog>
  );
}

const actionNames: Record<string, string> = {
  baseline: "History started",
  "source-import": "Map imported",
  "source-link": "Source linked",
  "source-refresh": "Source refreshed",
  create: "Map created",
  import: "Map imported",
  import_source: "Map imported",
  save: "Draft saved",
  update: "Draft saved",
  publish: "Map published",
  unpublish: "Map unpublished",
  restore: "Revision restored",
  refresh_source: "Source refreshed",
  source_refresh: "Source refreshed",
  link_source: "Source linked",
  source_link: "Source linked",
};
function historyDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}

export interface MapHistoryDialogProps {
  mapId: string;
  currentRevision: number;
  publishedRevision: number | null;
  canRestore: boolean;
  busy?: boolean;
  error?: string;
  returnFocusTo?: HTMLElement | null;
  onRestore: (revision: number) => void | Promise<void>;
  onClose: () => void;
}

export function MapHistoryDialog({
  mapId,
  currentRevision,
  publishedRevision,
  canRestore,
  busy = false,
  error,
  returnFocusTo,
  onRestore,
  onClose,
}: MapHistoryDialogProps) {
  const [entries, setEntries] = useState<MapHistoryEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState("");
  const [selected, setSelected] = useState<MapHistoryEntry | null>(null);
  const generation = useRef(0);
  const restoringRef = useRef(false);
  const confirmationRef = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    if (selected) confirmationRef.current?.focus({ preventScroll: true });
  }, [selected]);
  const load = async (before?: number) => {
    const requestGeneration = generation.current;
    setLoading(true);
    setLoadError("");
    try {
      const result = await api.history(mapId, before);
      if (generation.current !== requestGeneration) return;
      setEntries((previous) =>
        before === undefined
          ? result.history
          : [
              ...previous,
              ...result.history.filter(
                (entry) =>
                  !previous.some((item) => item.revision === entry.revision),
              ),
            ],
      );
      setNextBefore(result.nextBefore);
    } catch (cause) {
      if (generation.current === requestGeneration)
        setLoadError(errorText(cause));
    } finally {
      if (generation.current === requestGeneration) setLoading(false);
    }
  };
  useEffect(() => {
    generation.current++;
    restoringRef.current = false;
    setRestoring(false);
    setRestoreError("");
    setEntries([]);
    setNextBefore(null);
    setSelected(null);
    void load();
    return () => {
      generation.current++;
    };
  }, [mapId]);
  const pending = busy || restoring;
  const restore = async () => {
    if (
      !selected ||
      !canRestore ||
      selected.revision === currentRevision ||
      pending ||
      restoringRef.current
    )
      return;
    restoringRef.current = true;
    const restoreGeneration = generation.current;
    setRestoring(true);
    setRestoreError("");
    try {
      await onRestore(selected.revision);
    } catch (cause) {
      if (generation.current === restoreGeneration)
        setRestoreError(errorText(cause));
    } finally {
      if (generation.current === restoreGeneration) {
        restoringRef.current = false;
        setRestoring(false);
      }
    }
  };
  return (
    <ReviewDialog
      title="Version history"
      description="Saved versions of this map. Restore an earlier version as a new draft; the published map stays unchanged."
      busy={pending}
      returnFocusTo={returnFocusTo}
      onClose={onClose}
      footer={
        selected ? (
          <>
            <button
              type="button"
              className="em-button em-button-secondary"
              disabled={pending}
              onClick={() => {
                const revision = selected.revision;
                setSelected(null);
                setRestoreError("");
                queueMicrotask(() =>
                  document
                    .querySelector<HTMLButtonElement>(
                      `[data-restore-revision="${revision}"]`,
                    )
                    ?.focus({ preventScroll: true }),
                );
              }}
            >
              Back to history
            </button>
            <button
              type="button"
              className="em-button"
              disabled={
                pending || !canRestore || selected.revision === currentRevision
              }
              onClick={() => void restore()}
            >
              {pending ? (
                <LoaderCircle
                  size={15}
                  className="em-spin"
                  aria-hidden="true"
                />
              ) : (
                <History size={15} aria-hidden="true" />
              )}
              {pending ? "Restoring…" : "Restore as draft"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="em-button em-button-secondary"
            onClick={onClose}
            disabled={pending}
          >
            Done
          </button>
        )
      }
    >
      {(error || restoreError) && <Notice>{error || restoreError}</Notice>}
      {selected ? (
        <section className="em-map-history-confirm" aria-live="polite">
          <History size={26} aria-hidden="true" />
          <h3 ref={confirmationRef} tabIndex={-1}>
            Restore version {selected.revision}?
          </h3>
          <p>
            This brings back the map saved on {historyDate(selected.createdAt)}.
            Your current saved version remains in history.
          </p>
          <p>Review the restored draft before publishing it.</p>
        </section>
      ) : (
        <>
          {loadError && <Notice>{loadError}</Notice>}
          {entries.length > 0 && (
            <ol className="em-map-history-list">
              {entries.map((entry) => (
                <li key={entry.revision}>
                  <div className="em-map-history-topline">
                    <strong>Version {entry.revision}</strong>
                    {entry.revision === currentRevision && (
                      <span className="em-map-history-badge">
                        <Check size={12} aria-hidden="true" />
                        Current draft
                      </span>
                    )}
                    {entry.revision === publishedRevision && (
                      <span className="em-map-history-badge" data-published>
                        Published
                      </span>
                    )}
                  </div>
                  <h3>
                    {actionNames[entry.action] || fieldLabel(entry.action)}
                  </h3>
                  {entry.summary && <p>{entry.summary}</p>}
                  <div className="em-map-history-meta">
                    <span>
                      <Clock3 size={12} aria-hidden="true" />
                      <time dateTime={entry.createdAt}>
                        {historyDate(entry.createdAt)}
                      </time>
                    </span>
                    <span>
                      {entry.actor?.email ||
                        entry.actor?.id ||
                        "Unknown editor"}
                    </span>
                  </div>
                  {canRestore && entry.revision !== currentRevision && (
                    <button
                      type="button"
                      className="em-button em-button-secondary em-map-history-restore"
                      data-restore-revision={entry.revision}
                      onClick={() => {
                        setSelected(entry);
                        setRestoreError("");
                      }}
                      disabled={pending || loading}
                    >
                      Restore version {entry.revision}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          )}
          {loading ? (
            <Loading label="Loading version history…" />
          ) : (
            <>
              {!entries.length && !loadError && (
                <p className="em-map-review-empty">
                  <History size={20} aria-hidden="true" />
                  No saved versions yet.
                </p>
              )}
              {(nextBefore !== null || loadError) && (
                <button
                  type="button"
                  className="em-button em-button-secondary em-map-history-more"
                  onClick={() =>
                    void load(
                      entries.length ? (nextBefore ?? undefined) : undefined,
                    )
                  }
                >
                  {loadError ? "Try again" : "Load older versions"}
                </button>
              )}
            </>
          )}
        </>
      )}
    </ReviewDialog>
  );
}
